import { css } from '@emotion/react'
import { Chip, ClickAwayListener, MenuList, Paper, Popper } from '@mui/material'
import { Ref, SyntheticEvent, memo, useCallback } from 'react'
import {
    SEARCHSTATE,
    deleteSearchHistoryItem,
    resetSearchHistory,
    updateSearchQuery,
} from '../../../state/state.search'
import i18n from '../../../utilities/i18n'
import Button from '../../Buttons/Button'
import { useHistory } from '../../Hooks/useSearch'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { maxWindowWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import { APP_FONT, PAPER_HEIGHTS } from '../../theme'

const popperRootStyles = css({
    borderRadius: `0 0 ${shape.borderRadius}px ${shape.borderRadius}px`,
    display: 'flex',
    zIndex: PAPER_HEIGHTS.performance,
    background: palette.background,
    width: '100%',
    '&.MuiPopper-root': {
        [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
            width: '100%',
            left: '0px',
            right: '0px',
            transform: 'none',
        },
    },
})

const paperRootStyles = css({
    borderLeft: `1px solid ${palette.drawerDivider}`,
    borderRadius: `0 0 ${shape.borderRadius}px ${shape.borderRadius}px`,
    borderRight: `1px solid ${palette.drawerDivider}`,
    boxShadow: `0px 3px 3px 0px ${palette.drawerDivider}`,
    flex: 'auto',
    width: '100%',
    '& .MuiList-root': {
        fontFamily: APP_FONT,
    },
})

const headerContainerStyles = css({
    padding: `${getThemeSpacing(1)} ${getThemeSpacing(2)} ${getThemeSpacing(0.75)}`,
    borderBottom: `1px solid ${palette.drawerDivider}`,
    background: palette.background,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        padding: `${getThemeSpacing(0.75)} ${getThemeSpacing(1.5)} ${getThemeSpacing(0.5)}`,
    },
})

const headerTextStyles = css({
    fontSize: 12,
    fontWeight: 600,
    color: palette.disabledText,
    fontFamily: APP_FONT,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        fontSize: 11,
    },
})

const tagsContainerStyles = css({
    display: 'flex',
    flexWrap: 'wrap',
    gap: getThemeSpacing(0.75),
    maxHeight: '160px',
    overflowY: 'auto',
    padding: `${getThemeSpacing(1)} ${getThemeSpacing(2)} ${getThemeSpacing(0.75)}`,
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        padding: `${getThemeSpacing(0.75)} ${getThemeSpacing(1.5)} ${getThemeSpacing(0.5)}`,
        maxHeight: '120px',
        gap: getThemeSpacing(0.5),
        '& .MuiChip-label': {
            paddingRight: getThemeSpacing(0.75),
            fontWeight: 500,
            fontSize: '.8rem',
        },
    },
})

const historyChipStyles = css({
    font: 'inherit',
    padding: `0 ${getThemeSpacing(0.5)}`,
    marginBottom: 0,
    background: palette.tagsBackground,
    color: palette.tagsColor,
    fontWeight: 500,
    fontSize: 12,
    border: `1px solid ${palette.tagsBorder}`,
    cursor: 'pointer',
    height: 28,
    '&:hover': {
        background: palette.tagsBorder,
    },
    '&.MuiChip-root': {
        borderRadius: 16,
    },
    '& .MuiChip-deleteIcon': {
        marginLeft: 4,
        marginRight: 0,
        color: palette.disabledText,
        '&:hover': {
            color: palette.tagsColor,
        },
    },
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        fontSize: 11,
        height: 26,
        padding: `0 ${getThemeSpacing(0.375)}`,
    },
})

const clearAllStyles = css({
    width: 'calc(100% - 32px)',
    color: palette.greenMFC,
    fontSize: 12,
    fontWeight: 500,
    margin: `${getThemeSpacing(0.75)} ${getThemeSpacing(2)} ${getThemeSpacing(1)}`,
    minHeight: 32,
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        width: 'calc(100% - 24px)',
        margin: `${getThemeSpacing(0.5)} ${getThemeSpacing(1.5)} ${getThemeSpacing(0.75)}`,
        fontSize: 11,
        minHeight: 28,
    },
})

const paperNoBackgroundStyles = css({
    background: 'none',
})

const menuListStyles = css({
    padding: 0,
    '&:focus-visible': {
        outline: 'none',
    },
})

interface IInputHistory {
    anchorEl?: HTMLElement
    autoFocus?: boolean
    historyId?: string
    inputRef?: Ref<HTMLInputElement>
    list?: Array<string>
    location?: string
    open?: boolean
    page?: string
    onClose?(event: any, reason: any): void
    onDeleteHistoryItem?(item: string, history?: Array<any>): void
    onEmptyHistory?(history?: Array<any>): void
    onItemClick?(event: any, value: string): void
    setOpenHistory?(isOpen: boolean): void
}

const InputHistory = (props: IInputHistory) => {
    const {
        anchorEl,
        autoFocus,
        open = false,
        page,
        onDeleteHistoryItem = () => {},
        onEmptyHistory = () => {},
        onItemClick = () => {},
        setOpenHistory = () => {},
    } = props
    const history = useHistory()
    const hasHistory = history && history.length !== 0

    const refRectWidth = anchorEl?.getBoundingClientRect()?.width
    const paperStyles = page === 'search' ? paperNoBackgroundStyles : undefined

    const handleChipClick = useCallback(
        (value: string) => () => {
            onItemClick(null, value)
            setOpenHistory(false)
        },
        [onItemClick, setOpenHistory],
    )

    const handleChipDelete = useCallback(
        (value: string) => () => {
            const index = history?.findIndex((item) => item === value) ?? -1
            if (index !== -1) {
                if (SEARCHSTATE.query.search === value) {
                    updateSearchQuery({ search: '' })
                }
                deleteSearchHistoryItem(index, onDeleteHistoryItem)
            }
        },
        [history, onDeleteHistoryItem],
    )

    const onButtonClearAllClick = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            updateSearchQuery({ search: '', type: null })
            resetSearchHistory(onEmptyHistory)
        },
        [onEmptyHistory],
    )

    const closePopper = useCallback(
        () => setOpenHistory(false),
        [setOpenHistory],
    )

    return (
        hasHistory && (
            <Popper
                anchorEl={anchorEl}
                css={popperRootStyles}
                disablePortal
                open={open}
                placement={'bottom-start'}
                style={{ width: refRectWidth }}
            >
                <Paper
                    css={[paperRootStyles, paperStyles]}
                    data-testid="Container__InputHistory"
                    id="menu-list-grow"
                >
                    <ClickAwayListener onClickAway={closePopper}>
                        <MenuList autoFocus={autoFocus} css={menuListStyles}>
                            <div css={headerContainerStyles}>
                                <span css={headerTextStyles}>
                                    Recent Searches
                                </span>
                            </div>
                            <div css={tagsContainerStyles}>
                                {history?.map(
                                    (value: string, index: number) => (
                                        <Chip
                                            css={historyChipStyles}
                                            data-testid={`HistoryTag__${value}`}
                                            key={`${value}-${index}`}
                                            label={value}
                                            onClick={handleChipClick(value)}
                                            onDelete={handleChipDelete(value)}
                                        />
                                    ),
                                )}
                            </div>
                            <Button
                                border
                                clean
                                css={clearAllStyles}
                                data-testid="Button__ClearAll"
                                onClick={onButtonClearAllClick}
                                size="medium"
                            >
                                {i18n.t('search.clearAll')}
                            </Button>
                        </MenuList>
                    </ClickAwayListener>
                </Paper>
            </Popper>
        )
    )
}

export default memo(InputHistory)
